import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { Unstable_Popup as BasePopup } from '@mui/base/Unstable_Popup'
import { ClickAwayListener } from '@mui/base'
import { LuBell } from 'react-icons/lu'

import { fetchNotifications, markNotificationsSeen } from '../../features/notifications/notifications'
import {
  selectUnreadCount,
  selectNotificationItems,
  selectNotificationsMeta,
  selectNotificationsListStatus,
  selectNextOffset,
} from '../../features/notifications/selectors'
import { storedRole } from '../../features/notifications/audience'
import { bellContactPath } from '../../features/notifications/links'
import { useNotificationsPoll } from '../../hooks/useNotificationsPoll'
import { ACTIVE_OFFICE_KEY } from '../../lib/ambientHeaders'
import { timeAgo } from '../../utils/date'

const badgeText = (count) => (count > 99 ? '99+' : String(count))

const clientName = (client) => [client?.firstName, client?.lastName].filter(Boolean).join(' ') || 'Unknown client'

const NotificationBell = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const unreadCount = useSelector(selectUnreadCount)
  const items = useSelector(selectNotificationItems)
  const meta = useSelector(selectNotificationsMeta)
  const listStatus = useSelector(selectNotificationsListStatus)
  const nextOffset = useSelector(selectNextOffset)
  const [role] = useState(storedRole)
  const [anchor, setAnchor] = useState(null)
  const buttonRef = useRef(null)
  const openRef = useRef(false)
  const open = Boolean(anchor)

  useNotificationsPoll()

  const close = useCallback(({ restoreFocus = false } = {}) => {
    openRef.current = false
    setAnchor(null)
    if (restoreFocus) buttonRef.current?.focus()
  }, [])

  const focusPanel = useCallback((node) => {
    if (node) node.focus({ preventScroll: true })
  }, [])

  useEffect(() => {
    if (!open) return undefined

    const onKeyDown = (event) => {
      if (event.key === 'Escape') close({ restoreFocus: true })
    }

    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [open, close])

  const openPanel = async (target) => {
    openRef.current = true
    setAnchor(target)
    const result = await dispatch(fetchNotifications({ offset: 0 }))
    const newest = result.payload?.notifications?.[0]
    if (openRef.current && fetchNotifications.fulfilled.match(result) && newest) {
      dispatch(markNotificationsSeen({ until: newest.createdAt }))
    }
  }

  const toggle = (event) => (open ? close() : openPanel(event.currentTarget))

  const otherOfficeOf = (item) => {
    const activeOfficeId = localStorage.getItem(ACTIVE_OFFICE_KEY)
    return role === 'affiliate' && activeOfficeId && item.office?.id && item.office.id !== activeOfficeId
      ? item.office
      : null
  }

  const openItem = (item) => {
    if (!item.client?.id) return

    const otherOffice = otherOfficeOf(item)
    if (otherOffice) {
      localStorage.setItem(ACTIVE_OFFICE_KEY, otherOffice.id)
      window.location.assign(bellContactPath(item.client.id))
      return
    }

    close()
    navigate(bellContactPath(item.client.id))
  }

  const closeOnOutsideClick = (event) => {
    if (!buttonRef.current?.contains(event.target)) close()
  }

  const label = unreadCount > 0 ? `Notifications, ${unreadCount} unread` : 'Notifications'
  const canShowMore = Boolean(meta) && nextOffset < meta.total

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={toggle}
        aria-label={label}
        aria-haspopup="dialog"
        aria-expanded={open}
        className="relative flex h-9 w-9 items-center justify-center rounded-full text-text-secondary bg-black/[0.04] hover:bg-black/[0.08] dark:bg-white/[0.06] dark:hover:bg-white/[0.12] hover:text-text-primary transition-colors"
      >
        <LuBell size={18} />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 inline-flex min-w-[18px] justify-center rounded-full px-1 text-[11px] leading-4 font-semibold bg-primary-static-error-700 text-white">
            {badgeText(unreadCount)}
          </span>
        )}
      </button>
      <BasePopup anchor={anchor} open={open} placement="bottom-end" className="z-40">
        <ClickAwayListener onClickAway={closeOnOutsideClick}>
          <div
            ref={focusPanel}
            role="dialog"
            aria-label="Notifications"
            tabIndex={-1}
            className="mt-2 w-[380px] max-h-[min(480px,calc(100vh-96px))] overflow-y-auto bg-paper border border-divider rounded-md shadow-sm focus:outline-none"
          >
            <div className="px-4 py-3 border-b border-divider text-sm font-semibold text-text-primary">Notifications</div>
            {items.length === 0 && listStatus !== 'loading' ? (
              <p className="px-4 py-6 text-sm text-text-secondary">You're all caught up.</p>
            ) : (
              <ul>
                {items.map((item) => {
                  const otherOffice = otherOfficeOf(item)
                  return (
                    <li key={item.id}>
                      <button
                        type="button"
                        onClick={() => openItem(item)}
                        className="w-full text-left px-4 py-3 border-b border-divider hover:bg-black/[0.04] dark:hover:bg-white/[0.04]"
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className={`truncate text-sm text-text-primary ${item.unread ? 'font-semibold' : ''}`}>
                            {item.unread && <span className="sr-only">Unread: </span>}
                            {clientName(item.client)}{otherOffice?.name ? ` · ${otherOffice.name}` : ''}
                          </span>
                          {item.unread && <span aria-hidden="true" className="h-2 w-2 shrink-0 rounded-full bg-primary-static-error-700" />}
                        </div>
                        <p className="mt-0.5 line-clamp-2 text-sm text-text-secondary">{item.text}</p>
                        <p className="mt-0.5 text-xs text-text-secondary">{timeAgo(item.createdAt)}</p>
                      </button>
                    </li>
                  )
                })}
              </ul>
            )}
            {canShowMore && (
              <button
                type="button"
                onClick={() => dispatch(fetchNotifications({ offset: nextOffset }))}
                className="w-full px-4 py-2 text-sm text-text-primary hover:bg-black/[0.04] dark:hover:bg-white/[0.04]"
              >
                Show more
              </button>
            )}
          </div>
        </ClickAwayListener>
      </BasePopup>
    </>
  )
}

export default NotificationBell
